import { Component, forwardRef, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';
import { DestroySubscribers } from '../../../decorators/destroy-subscribers.decorator';
import { IUrlParams } from '../offer-url-params/offer-url-params.component';

@Component({
  templateUrl: './offer-url-params-affiliate.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OfferUrlParamsAffiliateComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => OfferUrlParamsAffiliateComponent),
      multi: true,
    },
  ],
})
@DestroySubscribers()
export class OfferUrlParamsAffiliateComponent implements ControlValueAccessor, OnInit, Validator {
  public form = new ParamsFormArray([], Validators.required);
  public subscribers: Record<string, Subscription> = {};
  public data: Observable<Record<string, IUrlParams>>;
  public titleData = {};
  private dataMap: Record<string, IUrlParams> = {};
  public onChange: (value: any) => void = () => {};
  public onTouch = () => {};

  public ngOnInit(): void {
    this.subscribers.formChanges = this.form.valueChanges.pipe(debounceTime(200)).subscribe((values) => {
      const data = values.reduce(
        (acc, item) => (
          (acc[+item.title] = item.keys?.map((key) => this.dataMap[+item.title]?.available_values?.findIndex((val) => val.value === key))),
          acc
        ),
        {}
      );
      this.onChange(JSON.stringify(data));
      this.onTouch();
    });
    this.data.subscribe((data) => {
      this.dataMap = {};
      this.titleData = {};
      this.form.reset();
      if (data && Object.keys(data)?.length) {
        this.dataMap = data;
        this.titleData = Object.keys(data).reduce((acc, item) => ((acc[item] = data[item].title), acc), {});
      }
    });
  }

  public addForm(titleIdx = null, keys: string[] = []): void {
    const titleCtr = new UntypedFormControl(titleIdx);
    const idx = this.form.controls.length;
    titleCtr.setValidators([
      Validators.required,
      () =>
        this.form.controls.some(
          (groupCtr, j) => groupCtr.controls.title.value && j !== idx && +groupCtr.controls.title.value === +titleCtr.value
        )
          ? { customValidate: 'key already exist' }
          : null,
    ]);
    const group = new ParamsFormGroup({
      title: titleCtr,
      keys: new UntypedFormControl(keys, Validators.required),
    });
    group.titleIdx = titleIdx;
    group.data = titleCtr.valueChanges.pipe(
      startWith(titleCtr.value),
      map((i) => {
        try {
          if (group.titleIdx !== i) {
            group.controls.keys.reset();
          }
          group.titleIdx = i;
          try {
            return this.dataMap[+i]?.available_values.reduce((acc, item) => ((acc[item.value] = item.label), acc), {}) || {};
          } catch {
            return {};
          }
        } catch {
          return {};
        }
      })
    );
    this.form.push(group);
  }

  public removeForm(i: number): void {
    this.form.removeAt(i);
  }

  public validate(): ValidationErrors | null {
    return this.form.invalid ? { errors: true } : null;
  }

  public writeValue(obj: any): void {
    try {
      const data: IData = JSON.parse(obj);
      Object.entries(data).forEach(([key, values]) => {
        const currData = this.dataMap[+key];
        const keys = currData.available_values.filter((_, i) => values.includes(i)).map(({ value }) => value);
        this.addForm(+key, keys);
      });
    } catch {}
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
}

class ParamsFormGroup extends UntypedFormGroup {
  controls: {
    title: UntypedFormControl;
    keys: UntypedFormControl;
  };
  data: Observable<any>;
  titleIdx: string;
}

class ParamsFormArray extends UntypedFormArray {
  controls: ParamsFormGroup[];
}

interface IData {
  [key: number]: number[];
}
